import React, { useEffect, useState } from "react";
import { CheckCircle, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

import { gmaoInstallationWorkflow } from "@/api";
import { Button } from "@/components/ui/button";


function toInputValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function formatDate(value) {
  if (!value) return "Non renseignée";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" });
}


export default function PublicInstallationDeliveryCard({ token }) {
  const [context, setContext] = useState(null);
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [proposedDate, setProposedDate] = useState("");
  const [message, setMessage] = useState("");

  const load = async () => {
    try {
      const payload = await gmaoInstallationWorkflow.fetchPublicDelivery(token);
      setContext(payload);
      setAvailable(true);
    } catch (error) {
      if (error?.response?.status === 404) {
        setAvailable(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading || !available || !context) return null;

  const respond = async (action) => {
    if (action === "request_reschedule" && !proposedDate) {
      toast.error("Choisissez une nouvelle date.");
      return;
    }

    setBusy(true);
    try {
      const payload = await gmaoInstallationWorkflow.respondPublicDelivery(token, {
        action,
        proposed_date: action === "request_reschedule"
          ? new Date(proposedDate).toISOString()
          : null,
        message: message.trim(),
      });
      setContext(payload);
      setShowReschedule(false);
      toast.success(
        action === "confirm"
          ? "Livraison confirmée."
          : "Votre proposition a été envoyée."
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Réponse impossible à enregistrer.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mx-4 mt-4 rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 shadow-sm sm:mx-6"
      data-testid="public-installation-delivery-confirmation"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="label-eyebrow">Livraison et installation</div>
          <h2 className="mt-1 font-heading text-lg font-black text-[var(--do-text)]">
            Confirmez la date proposée
          </h2>
          <p className="mt-1 text-sm text-[var(--do-muted)]">
            {formatDate(context.deliveryDate)}
          </p>
        </div>

        {context.status === "confirmed" && (
          <span className="inline-flex items-center gap-2 rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-sm font-black text-[var(--do-accent)]">
            <CheckCircle size={18} weight="fill" /> Confirmée
          </span>
        )}
      </div>

      {context.status === "pending" && !showReschedule && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <Button disabled={busy} onClick={() => respond("confirm")}>
            <CheckCircle size={17} /> Confirmer la livraison
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => {
            setProposedDate(toInputValue(context.deliveryDate));
            setShowReschedule(true);
          }}>
            <CalendarBlank size={17} /> Proposer une nouvelle date
          </Button>
        </div>
      )}

      {showReschedule && (
        <div className="mt-4 grid gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
          <label className="grid gap-1 text-sm font-bold text-[var(--do-text)]">
            Nouvelle date
            <input
              type="datetime-local"
              value={proposedDate}
              onChange={(event) => setProposedDate(event.target.value)}
              className="h-11 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 font-normal outline-none focus:border-[var(--do-accent)]"
            />
          </label>
          <label className="grid gap-1 text-sm font-bold text-[var(--do-text)]">
            Message facultatif
            <textarea
              rows={3}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 font-normal outline-none focus:border-[var(--do-accent)]"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={busy} onClick={() => setShowReschedule(false)}>
              Annuler
            </Button>
            <Button disabled={busy} onClick={() => respond("request_reschedule")}>
              Envoyer la proposition
            </Button>
          </div>
        </div>
      )}

      {context.status === "reschedule_requested" && (
        <div className="mt-4 rounded-xl border border-[var(--do-border)] bg-[var(--do-surface-soft)] p-4">
          <div className="text-sm font-black text-[var(--do-text)]">Nouvelle date proposée</div>
          <div className="mt-1 text-sm text-[var(--do-muted)]">{formatDate(context.proposedDate)}</div>
          <p className="mt-2 text-xs text-[var(--do-muted)]">
            Votre contact doit maintenant reprendre cette proposition avant une nouvelle confirmation.
          </p>
        </div>
      )}
    </section>
  );
}
